/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Badge, Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import TableComponent from '../comman/component/table';
import FooterEnd from '../comman/component/footerEnd';
import { searchContactAuditLogs } from '../services/contactAuditLog';
import { formatPhoneDisplay } from '../helper/phone';
import useExportData from '../hooks/useExportData';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BASE_STORAGE_URL } from '../helper/constant';
import { CHECK_MARK_SYMBOL } from '../helper/constant/contact';

const MODULE_OPTIONS = [
  { value: '*', label: 'All Modules' },
  { value: 'CONTACT', label: 'Contacts' },
  { value: 'GALLERY', label: 'Gallery (All)' },
  { value: 'GALLERY_FULLSCREEN', label: 'Gallery - Full Screen' },
  { value: 'GALLERY_THUMBNAIL', label: 'Gallery - Thumbnail' },
  { value: 'USER', label: 'User Management' },
];

const ACTION_OPTIONS = [
  { value: '*', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
];

const renderActionBadge = (action) => {
  switch (action?.toUpperCase()) {
    case 'CREATE':
      return (
        <span
          className='badge bg-success'
          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '12px' }}
        >
          CREATE
        </span>
      );
    case 'UPDATE':
    case 'UPDATE_AVATAR':
      return (
        <span
          className='badge bg-primary'
          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '12px' }}
        >
          UPDATE
        </span>
      );
    case 'DELETE':
      return (
        <span
          className='badge bg-danger'
          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '12px' }}
        >
          DELETE
        </span>
      );
    default:
      return (
        <span
          className='badge bg-secondary'
          style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '12px' }}
        >
          {action || 'UNKNOWN'}
        </span>
      );
  }
};

const getGalleryTypeFromRow = (row) => {
  if (!row) return null;
  try {
    const newVals = row.newValues ? (typeof row.newValues === 'string' ? JSON.parse(row.newValues) : row.newValues) : null;
    if (newVals && newVals.galleryType) return newVals.galleryType;
    const oldVals = row.oldValues ? (typeof row.oldValues === 'string' ? JSON.parse(row.oldValues) : row.oldValues) : null;
    if (oldVals && oldVals.galleryType) return oldVals.galleryType;
  } catch (e) {
    // ignore
  }
  return null;
};

const renderEntityBadge = (entityType, row) => {
  const type = entityType?.toUpperCase() || 'CONTACT';

  if (type === 'GALLERY_THUMBNAIL' || (type === 'GALLERY' && getGalleryTypeFromRow(row) === 'circle')) {
    return (
      <span
        className='badge'
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          borderRadius: '10px',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          border: '1px solid #fde68a',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <i className='fas fa-image'></i> Gallery - Thumbnail
      </span>
    );
  }

  if (type === 'GALLERY_FULLSCREEN' || type === 'GALLERY') {
    return (
      <span
        className='badge'
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          borderRadius: '10px',
          backgroundColor: '#fffbeb',
          color: '#b45309',
          border: '1px solid #fcd34d',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <i className='fas fa-image'></i> Gallery - Full Screen
      </span>
    );
  }

  if (type === 'USER') {
    return (
      <span
        className='badge'
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          borderRadius: '10px',
          backgroundColor: '#ede9fe',
          color: '#5b21b6',
          border: '1px solid #ddd6fe',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <i className='fas fa-user-shield'></i> USER
      </span>
    );
  }

  return (
    <span
      className='badge'
      style={{
        fontSize: '11px',
        padding: '4px 8px',
        borderRadius: '10px',
        backgroundColor: '#e0f2fe',
        color: '#0369a1',
        border: '1px solid #bae6fd',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
      }}
    >
      <i className='fas fa-address-book'></i> CONTACT
    </span>
  );
};

const ContactAuditLog = ({ userRole }) => {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [selectedModule, setSelectedModule] = useState('*');
  const [selectedAction, setSelectedAction] = useState('*');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Detail Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        q: searchText.trim() || '*',
        entityType: selectedModule !== '*' ? selectedModule : undefined,
        action: selectedAction !== '*' ? selectedAction : undefined,
        fromDate: startDate ? moment(startDate).startOf('day').valueOf() : undefined,
        toDate: endDate ? moment(endDate).endOf('day').valueOf() : undefined,
        page: 0,
        size: 200,
        sortType: 'desc',
      };

      const response = await searchContactAuditLogs(params);
      if (response && response.status === 200 && response.data) {
        setLogs(response.data.objects || []);
        setTotalCount(response.data.total || (response.data.objects ? response.data.objects.length : 0));
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchText, selectedModule, selectedAction, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleOpenDetails = (row) => {
    setSelectedLog(row);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
    setShowModal(false);
  };

  const handleResetFilters = () => {
    setSearchText('');
    setSelectedModule('*');
    setSelectedAction('*');
    setStartDate(null);
    setEndDate(null);
  };

  const columns = [
    {
      name: 'Time',
      selector: (row) => row.createdAt,
      sortable: true,
      sortFunction: (rowA, rowB) => (rowA.createdAt || 0) - (rowB.createdAt || 0),
      width: '180px',
      cell: (row) => (
        <div>
          <div style={{ fontWeight: '600', fontSize: '13px' }}>
            {row.createdAt ? moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </div>
          <small className='text-muted'>
            {row.createdAt ? moment(row.createdAt).fromNow() : ''}
          </small>
        </div>
      ),
      formatValue: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
      key: 'createdAt',
    },
    {
      name: 'User',
      selector: (row) => row.username,
      sortable: true,
      width: '180px',
      cell: (row) => (
        <span
          style={{
            fontWeight: '500',
            color: '#1f2937',
            fontSize: '13px',
            display: 'inline-block',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={row.username}
        >
          {row.username}
        </span>
      ),
      key: 'username',
    },
    {
      name: 'Module',
      selector: (row) => row.entityType || 'CONTACT',
      sortable: true,
      width: '185px',
      cell: (row) => renderEntityBadge(row.entityType, row),
      key: 'entityType',
    },
    {
      name: 'Action',
      selector: (row) => row.action,
      sortable: true,
      width: '110px',
      cell: (row) => renderActionBadge(row.action),
      key: 'action',
    },
    {
      name: 'Target / Name',
      selector: (row) => row.targetName || row.contactName || '-',
      sortable: true,
      width: '180px',
      cell: (row) => {
        const target = row.targetName || row.contactName || '-';
        return (
          <span style={{ fontWeight: '500', color: '#111827' }} title={target}>
            {target}
          </span>
        );
      },
      key: 'targetName',
    },
    {
      name: 'Phone Number(s)',
      selector: (row) => row.phones || '-',
      sortable: true,
      width: '160px',
      cell: (row) => {
        if (!row.phones) return <span className='text-muted'>-</span>;
        const formatted = row.phones
          .split(',')
          .map((p) => formatPhoneDisplay(p.trim()))
          .join(', ');
        return <span style={{ fontSize: '13px' }}>{formatted}</span>;
      },
      key: 'phones',
    },
    {
      name: 'Change Summary',
      selector: (row) => row.description || '-',
      sortable: false,
      grow: 2,
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'normal',
            lineHeight: '1.4',
            fontSize: '13px',
            color: '#4b5563',
          }}
        >
          {row.description || '-'}
        </div>
      ),
      key: 'description',
    },
    {
      name: 'Details',
      button: true,
      preventExport: true,
      width: '90px',
      cell: (row) => (
        <Button
          variant='outline-secondary'
          size='sm'
          style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '4px' }}
          onClick={() => handleOpenDetails(row)}
        >
          <i className='fas fa-info-circle me-1'></i> Details
        </Button>
      ),
    },
  ];

  const { handleExport } = useExportData({
    filename: 'audit_logs',
    columns,
  });

  const parseJsonSafe = (str) => {
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  return (
    <div id='pageWrapper'>
      <div className='main-page'>
        <div className='page-wrapper'>
          <div className='header-topbar justify-content-between'></div>
          <section className='common-sec users-sec card-body-no-padding'>
            <div className='container-fluid'>
              <div className='row'>
                <div className='col-12'>
                  <Card className='shadow-sm border-0 mb-4'>
                    <Card.Header>
                      <div className='card-header-inner'>
                        <div className='card-header-inner-left'>
                          <h2>
                            Audit Logs
                            <Badge
                              bg='secondary'
                              className='ms-2'
                              style={{ fontSize: '13px', verticalAlign: 'middle' }}
                            >
                              {totalCount} records
                            </Badge>
                          </h2>
                        </div>
                        <div className='card-header-inner-right'>
                          <div className='contact-title d-flex align-items-center gap-2'>
                            <Button
                              className='btn-sm'
                              variant='outline-secondary'
                              onClick={fetchLogs}
                              disabled={isLoading}
                              title='Refresh Log Data'
                            >
                              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i> Refresh
                            </Button>
                            <Button
                              className='btn-sm'
                              variant='outline-primary'
                              onClick={handleExport}
                              disabled={isLoading || logs.length === 0}
                              title='Export Logs to CSV'
                            >
                              <i className='fas fa-file-export'></i> Export
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card.Header>

                    <Card.Body>
                      {/* Filter Toolbar */}
                      <div className='contact-filter mb-4 p-3 bg-light rounded-3'>
                        <Row className='g-3 align-items-end'>
                          <Col xs={12} md={3}>
                            <label className='form-label small fw-bold text-muted'>
                              Search
                            </label>
                            <div className='input-group'>
                              <input
                                className='form-control'
                                type='text'
                                name='search'
                                placeholder='Search by user, target, phone...'
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                              />
                              {searchText && (
                                <button
                                  className='btn btn-outline-secondary'
                                  type='button'
                                  onClick={() => setSearchText('')}
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          </Col>

                          <Col xs={12} md={2}>
                            <label className='form-label small fw-bold text-muted'>
                              Module
                            </label>
                            <select
                              className='form-select'
                              value={selectedModule}
                              onChange={(e) => setSelectedModule(e.target.value)}
                            >
                              {MODULE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </Col>

                          <Col xs={12} md={2}>
                            <label className='form-label small fw-bold text-muted'>
                              Action
                            </label>
                            <select
                              className='form-select'
                              value={selectedAction}
                              onChange={(e) => setSelectedAction(e.target.value)}
                            >
                              {ACTION_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </Col>

                          <Col xs={6} md={2}>
                            <label className='form-label small fw-bold text-muted'>
                              From Date
                            </label>
                            <DatePicker
                              className='form-control'
                              selected={startDate}
                              onChange={(date) => setStartDate(date)}
                              selectsStart
                              startDate={startDate}
                              endDate={endDate}
                              placeholderText='Start date...'
                              dateFormat='yyyy-MM-dd'
                              isClearable
                            />
                          </Col>

                          <Col xs={6} md={2}>
                            <label className='form-label small fw-bold text-muted'>
                              To Date
                            </label>
                            <DatePicker
                              className='form-control'
                              selected={endDate}
                              onChange={(date) => setEndDate(date)}
                              selectsEnd
                              startDate={startDate}
                              endDate={endDate}
                              minDate={startDate}
                              placeholderText='End date...'
                              dateFormat='yyyy-MM-dd'
                              isClearable
                            />
                          </Col>

                          <Col xs={12} md={1} className='text-end'>
                            <Button
                              variant='link'
                              className='text-decoration-none text-muted btn-sm p-0'
                              onClick={handleResetFilters}
                            >
                              Clear
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      {/* Data Table */}
                      <div className='table-responsive'>
                        <TableComponent
                          title=''
                          columns={columns}
                          data={logs}
                          progressPending={isLoading}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>
          </section>
          <FooterEnd />
        </div>
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size='lg'
          centered
          className='audit-detail-modal'
        >
          <Modal.Header closeButton>
            <Modal.Title className='h6 fw-bold mb-0'>
              Log Details &bull; {renderActionBadge(selectedLog.action)}
              <span className='ms-2'>{renderEntityBadge(selectedLog.entityType, selectedLog)}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='mb-3 pb-3 border-bottom'>
              <Row className='g-2'>
                <Col sm={6}>
                  <small className='text-muted d-block'>Timestamp</small>
                  <strong>
                    {selectedLog.createdAt
                      ? moment(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </strong>
                </Col>
                <Col sm={6}>
                  <small className='text-muted d-block'>Performed By</small>
                  <strong>{selectedLog.username}</strong>
                </Col>
                <Col sm={6} className='mt-2'>
                  <small className='text-muted d-block'>Target / Name</small>
                  <strong>{selectedLog.targetName || selectedLog.contactName || '-'}</strong>
                </Col>
                <Col sm={6} className='mt-2'>
                  <small className='text-muted d-block'>Phone Number(s)</small>
                  <strong>
                    {selectedLog.phones
                      ? selectedLog.phones
                        .split(',')
                        .map((p) => formatPhoneDisplay(p.trim()))
                        .join(', ')
                      : '-'}
                  </strong>
                </Col>
                <Col sm={12} className='mt-2'>
                  <small className='text-muted d-block'>Summary</small>
                  <div className='p-2 bg-light rounded text-dark mt-1'>
                    {selectedLog.description || '-'}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Structured Field Comparison */}
            {(() => {
              const oldObj = parseJsonSafe(selectedLog.oldValues) || {};
              const newObj = parseJsonSafe(selectedLog.newValues) || {};
              const hasSnapshots = selectedLog.oldValues || selectedLog.newValues;

              if (!hasSnapshots) return null;

              const formatVal = (val) => {
                if (val === undefined || val === null || val === '') return <span className='text-muted'>-</span>;
                if (Array.isArray(val)) {
                  if (val.length === 0) return <span className='text-muted'>Empty</span>;
                  return val.join(', ');
                }
                return String(val);
              };

              const formatLinkAction = (type) => {
                if (!type) return 'Do nothing (One-Tap)';
                if (type === 'oneTap') return 'Do nothing (One-Tap)';
                if (type === 'autoDeclined') return 'End Call';
                if (type === 'autoAccepted') return 'Receive Call';
                return type;
              };

              const renderImageThumb = (name, avatar, isCircle = false) => {
                if (!name || name === 'None' || name === '-') {
                  return <span className='text-muted'>None</span>;
                }
                const imgUrl = avatar
                  ? (avatar.startsWith('http') ? avatar : `${BASE_STORAGE_URL}/${avatar}`)
                  : null;
                return (
                  <div className='d-flex align-items-center gap-2'>
                    {imgUrl && (
                      <img
                        src={imgUrl}
                        alt={name}
                        style={{
                          width: '32px',
                          height: '32px',
                          objectFit: 'cover',
                          borderRadius: isCircle ? '50%' : '4px',
                          border: '1px solid #dee2e6',
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <span style={{ fontWeight: '500' }}>{name}</span>
                  </div>
                );
              };

              const entityType = (selectedLog.entityType || 'CONTACT').toUpperCase();
              let compareRows = [];

              if (entityType.startsWith('GALLERY')) {
                compareRows = [
                  {
                    label: 'Image Name',
                    oldVal: oldObj.galleryName,
                    newVal: newObj.galleryName,
                  },
                  {
                    label: 'Gallery Type',
                    oldVal: oldObj.galleryType,
                    newVal: newObj.galleryType,
                    render: (val) => {
                      if (!val) return <span className='text-muted'>-</span>;
                      const isThumb = val.toLowerCase() === 'circle' || val.toLowerCase() === 'thumbnail';
                      return (
                        <Badge bg={isThumb ? 'warning' : 'info'} text='dark'>
                          {isThumb ? 'Thumbnail' : 'Full Screen'} ({val})
                        </Badge>
                      );
                    },
                  },
                  {
                    label: 'Image Preview',
                    oldVal: oldObj.avatar ? (oldObj.galleryName || 'Image Available') : undefined,
                    newVal: newObj.avatar ? (newObj.galleryName || 'Image Available') : undefined,
                    render: (val, isOld) => {
                      const avatar = isOld ? oldObj.avatar : newObj.avatar;
                      const name = (isOld ? oldObj.galleryName : newObj.galleryName) || 'Image';
                      return renderImageThumb(avatar ? name : 'None', avatar, false);
                    },
                  },
                ];
              } else if (entityType === 'USER') {
                compareRows = [
                  {
                    label: 'Username',
                    oldVal: oldObj.username,
                    newVal: newObj.username,
                  },
                  {
                    label: 'First Name',
                    oldVal: oldObj.firstName,
                    newVal: newObj.firstName,
                  },
                  {
                    label: 'Last Name',
                    oldVal: oldObj.lastName,
                    newVal: newObj.lastName,
                  },
                  {
                    label: 'Phone Number',
                    oldVal: oldObj.phone,
                    newVal: newObj.phone,
                    render: (val) => (val ? formatPhoneDisplay(val) : <span className='text-muted'>-</span>),
                  },
                  {
                    label: 'User Avatar',
                    oldVal: oldObj.avatar,
                    newVal: newObj.avatar,
                    render: (val, isOld) => {
                      const avatar = isOld ? oldObj.avatar : newObj.avatar;
                      return renderImageThumb(avatar ? 'Avatar' : 'None', avatar, true);
                    },
                  },
                ];
              } else {
                // CONTACT compare rows
                compareRows = [
                  {
                    label: 'Contact Name',
                    oldVal: oldObj.name,
                    newVal: newObj.name,
                  },
                  {
                    label: 'Prefix Symbol',
                    oldVal: oldObj.prefixName !== undefined ? (oldObj.prefixName ? oldObj.prefixName : 'Empty') : undefined,
                    newVal: newObj.prefixName !== undefined ? (newObj.prefixName ? newObj.prefixName : 'Empty') : undefined,
                    render: (val) => {
                      if (val === undefined || val === null || val === '') return <span className='text-muted'>-</span>;
                      if (val === 'Empty') return <span className='badge bg-light text-muted border'>Empty</span>;
                      return <Badge bg='info' text='dark' style={{ fontSize: '13px' }}>{val}</Badge>;
                    },
                  },
                  {
                    label: 'Checkmark Verified',
                    oldVal: oldObj.isCheckMark !== undefined ? (oldObj.isCheckMark ? 'Yes' : 'No') : undefined,
                    newVal: newObj.isCheckMark !== undefined ? (newObj.isCheckMark ? 'Yes' : 'No') : undefined,
                    render: (val) => {
                      if (val === undefined || val === null || val === '') return <span className='text-muted'>-</span>;
                      return val === 'Yes' ? (
                        <span className='text-success fw-bold'>
                          <i className='fas fa-check-circle me-1'></i> Yes {CHECK_MARK_SYMBOL}
                        </span>
                      ) : (
                        <span className='text-muted'>No</span>
                      );
                    },
                  },
                  {
                    label: 'Phone Numbers',
                    oldVal: oldObj.phones,
                    newVal: newObj.phones,
                    render: (val) => {
                      if (!val || (Array.isArray(val) && val.length === 0)) return <span className='text-muted'>None</span>;
                      const arr = Array.isArray(val) ? val : [val];
                      return arr.map((p) => formatPhoneDisplay(p)).join(', ');
                    },
                  },
                  {
                    label: 'Select Region',
                    oldVal: oldObj.regions,
                    newVal: newObj.regions,
                    render: (val) => {
                      if (!val || (Array.isArray(val) && val.length === 0)) return <span className='text-muted'>None</span>;
                      const arr = Array.isArray(val) ? val : [val];
                      return arr.join(', ');
                    },
                  },
                  {
                    label: '1-Tap Link Option',
                    oldVal: oldObj.linkActionType !== undefined ? formatLinkAction(oldObj.linkActionType) : undefined,
                    newVal: newObj.linkActionType !== undefined ? formatLinkAction(newObj.linkActionType) : undefined,
                  },
                  {
                    label: '1-Tap Link URL',
                    oldVal: oldObj.linkActionUrl,
                    newVal: newObj.linkActionUrl,
                    render: (val) => {
                      if (!val) return <span className='text-muted'>None</span>;
                      return (
                        <a href={val} target='_blank' rel='noopener noreferrer' style={{ wordBreak: 'break-all' }}>
                          {val}
                        </a>
                      );
                    },
                  },
                  {
                    label: 'Image (Background)',
                    oldVal: oldObj.galleryName || oldObj.galleryId || (oldObj.galleryAvatar ? 'Image Selected' : (oldObj.name ? 'None' : undefined)),
                    newVal: newObj.galleryName || newObj.galleryId || (newObj.galleryAvatar ? 'Image Selected' : (newObj.name ? 'None' : undefined)),
                    rawOld: oldObj.galleryId || oldObj.galleryName || (oldObj.galleryAvatar ? 'Image Selected' : undefined),
                    rawNew: newObj.galleryId || newObj.galleryName || (newObj.galleryAvatar ? 'Image Selected' : undefined),
                    render: (val, isOld) => {
                      const avatar = isOld ? oldObj.galleryAvatar : newObj.galleryAvatar;
                      return renderImageThumb(val, avatar, false);
                    },
                  },
                  {
                    label: 'Thumbnail (Avatar)',
                    oldVal: oldObj.circleGalleryName || oldObj.circleGalleryId || (oldObj.circleGalleryAvatar ? 'Avatar Selected' : (oldObj.name ? 'None' : undefined)),
                    newVal: newObj.circleGalleryName || newObj.circleGalleryId || (newObj.circleGalleryAvatar ? 'Avatar Selected' : (newObj.name ? 'None' : undefined)),
                    rawOld: oldObj.circleGalleryId || oldObj.circleGalleryName || (oldObj.circleGalleryAvatar ? 'Avatar Selected' : undefined),
                    rawNew: newObj.circleGalleryId || newObj.circleGalleryName || (newObj.circleGalleryAvatar ? 'Avatar Selected' : undefined),
                    render: (val, isOld) => {
                      const avatar = isOld ? oldObj.circleGalleryAvatar : newObj.circleGalleryAvatar;
                      return renderImageThumb(val, avatar, true);
                    },
                  },
                ];
              }

              const visibleRows = compareRows.filter(
                (r) => r.oldVal !== undefined || r.newVal !== undefined
              );

              if (visibleRows.length === 0) return null;

              return (
                <div className='mb-4'>
                  <h6 className='fw-bold mb-2'>
                    <i className='fas fa-columns text-primary me-2'></i>Field Changes (Diff)
                  </h6>
                  <div className='table-responsive border rounded'>
                    <table className='table table-sm table-hover align-middle mb-0' style={{ fontSize: '13px' }}>
                      <thead className='table-light'>
                        <tr>
                          <th style={{ width: '25%' }}>Field</th>
                          <th style={{ width: '37.5%' }}>Previous State</th>
                          <th style={{ width: '37.5%' }}>New State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((r, idx) => {
                          const isChanged =
                            r.rawOld !== undefined || r.rawNew !== undefined
                              ? JSON.stringify(r.rawOld) !== JSON.stringify(r.rawNew)
                              : JSON.stringify(r.oldVal) !== JSON.stringify(r.newVal);
                          return (
                            <tr key={idx} style={isChanged ? { backgroundColor: '#fffbeb' } : undefined}>
                              <td className='fw-semibold'>
                                {r.label}
                                {isChanged && (
                                  <Badge bg='warning' text='dark' className='ms-2' style={{ fontSize: '10px' }}>
                                    Modified
                                  </Badge>
                                )}
                              </td>
                              <td>{r.render ? r.render(r.oldVal, true) : formatVal(r.oldVal)}</td>
                              <td>{r.render ? r.render(r.newVal, false) : formatVal(r.newVal)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Technical Snapshot View */}
            <div className='mt-3'>
              <h6 className='fw-bold mb-2'>Technical Snapshot</h6>
              <Row>
                {selectedLog.oldValues && (
                  <Col md={selectedLog.newValues ? 6 : 12}>
                    <div className='p-2 border rounded bg-light'>
                      <small className='text-danger fw-bold d-block mb-1'>
                        Before (Previous State):
                      </small>
                      <pre
                        style={{
                          fontSize: '12px',
                          maxHeight: '220px',
                          overflowY: 'auto',
                          marginBottom: 0,
                        }}
                      >
                        {JSON.stringify(parseJsonSafe(selectedLog.oldValues), null, 2)}
                      </pre>
                    </div>
                  </Col>
                )}
                {selectedLog.newValues && (
                  <Col md={selectedLog.oldValues ? 6 : 12} className='mt-2 mt-md-0'>
                    <div className='p-2 border rounded bg-light'>
                      <small className='text-success fw-bold d-block mb-1'>
                        After (New State):
                      </small>
                      <pre
                        style={{
                          fontSize: '12px',
                          maxHeight: '220px',
                          overflowY: 'auto',
                          marginBottom: 0,
                        }}
                      >
                        {JSON.stringify(parseJsonSafe(selectedLog.newValues), null, 2)}
                      </pre>
                    </div>
                  </Col>
                )}
                {!selectedLog.oldValues && !selectedLog.newValues && (
                  <Col xs={12}>
                    <p className='text-muted small mb-0'>No snapshot data available.</p>
                  </Col>
                )}
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' size='sm' onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = ({ authReducer: { userRole } }) => ({
  userRole,
});

export default connect(mapStateToProps)(ContactAuditLog);
